import isEqual from "lodash.isequal"
import { createActions, createReducer } from "reduxsauce"
import Immutable from "seamless-immutable"

/* ------------- Types and Action Creators ------------- */

const { Types, Creators } = createActions({
  getRequestsDigitalReceiptRequest: ["payload"], // payload: { params }
  getRequestsDigitalReceiptSuccess: ["payload"], // payload: { result }

  updateRequestsDigitalReceiptStatusRequest: ["payload"], // payload: { id, status }
  updateRequestsDigitalReceiptStatusSuccess: ["payload"], // payload: { id, status }

  reviewDigitalReceiptUpdateRequest: ["payload"], // payload: { id, action: 'approve' | 'reject' }
  reviewDigitalReceiptUpdateSuccess: ["payload"], // payload: { id, action }

  requestsDigitalReceiptFailure: ["payload"],

  clearRequestsDigitalReceiptFilters: ["payload"],
  requestsDigitalReceiptSetProp: ["key", "value"],

  logout: null
})

export const RequestsDigitalReceiptTypes = Types
export default Creators

/* ------------- Initial State ------------- */

// Toggleable columns (Status is rendered first via getColumns; # comes from the base columns).
const columnsAdmin = [
  { field_id: "status", label: "Status", active: true },
  { field_id: "programs", label: "Programs", active: true },
  { field_id: "created_timestamp", label: "Request date", active: true },
  { field_id: "updated_timestamp", label: "Update status date", active: true },
  { field_id: "business_account", label: "Business account", active: true },
  { field_id: "user_full_name", label: "User full name", active: true },
  { field_id: "user_email", label: "User email", active: true }
]

export const INITIAL_STATE = Immutable({
  fetching: false,
  result: {
    count: 0,
    next: null,
    previous: null,
    results: []
  },

  // Filters offered in the "+ Add filter" menu (each must exist in filtersTypeByIdMap.digital_receipt).
  defaultColumnsAdmin: [
    { field_id: "request_status", label: "Status" },
    { field_id: "programs", label: "Programs" },
    { field_id: "business_account", label: "Business account" },
    { field_id: "user_full_name", label: "User full name" },
    { field_id: "user_email", label: "User email" },
    { field_id: "created_timestamp", label: "Request date" },
    { field_id: "updated_timestamp", label: "Update status date" }
  ],
  columnsAdmin: [...columnsAdmin],

  filtersLastUpd: null,
  filters: {
    request_status: "all",
    programs: "all",
    user_full_name: [],
    user_email: [],
    business_account: [],
    created_timestamp: null,
    updated_timestamp: null,
    exclude_fields: []
  },

  // Static filter options (no backend filter_options endpoint yet).
  filterOptions: {
    // Status is rendered as a tag in the dropdown, which expects plain string options.
    request_status: ["pending", "enrolled", "unenrolled", "rejected"],
    // Non-status filters accept {id, name} (id is the stored value, name the label).
    programs: [
      { id: "ethoca", name: "Consumer Clarity" },
      { id: "verifi", name: "Order Insight" }
    ]
  },

  visibleFiltersAdmin: {
    request_status: true,
    programs: true,
    user_email: true,
    user_full_name: true,
    business_account: true
  },

  saveResult: null
})

/* ------------- Selectors ------------- */

export const selectAreFiltersDefault = (state) =>
  isEqual(INITIAL_STATE.filters, state.requestsDigitalReceipt.filters)

/* ------------- Reducers ------------- */

const getRequestsDigitalReceiptRequest = (state) => state.merge({ fetching: true })
const getRequestsDigitalReceiptSuccess = (state, { payload: result }) => {
  if (result?.results) {
    return state.merge({ fetching: false, result })
  }
  return state.merge({
    fetching: false,
    result: { count: result?.length || 0, results: result || [] }
  })
}

const updateRequestsDigitalReceiptStatusRequest = (state) => state.merge({ fetching: true })
const updateRequestsDigitalReceiptStatusSuccess = (state, { payload: { id, status } }) => {
  const newResults = state.result.results?.map((el) =>
    el.id === id ? { ...el, status, request_status: status } : el
  )
  return state.merge({
    fetching: false,
    result: { ...state.result, results: newResults },
    saveResult: "updated"
  })
}

const reviewDigitalReceiptUpdateRequest = (state) => state.merge({ fetching: true })
const reviewDigitalReceiptUpdateSuccess = (state, { payload: { id, action } }) => {
  const newResults = state.result.results?.map((el) => {
    if (el.id !== id) return el
    // Approving applies the proposed values; rejecting drops the pending update. Either
    // way the row is no longer "needs update" / has no pending change. Keep status and
    // request_status in sync (the table reads status, the drawer reads request_status).
    const next = action === "approve" ? "enrolled" : el.status
    return {
      ...el,
      has_pending_update: false,
      profile_update: null,
      status: next,
      request_status: next
    }
  })
  return state.merge({
    fetching: false,
    result: { ...state.result, results: newResults },
    saveResult: action === "approve" ? "approved" : "rejected"
  })
}

const requestsDigitalReceiptFailure = (state) => state.merge({ fetching: false })

export const requestsDigitalReceiptSetProp = (state, { key, value }) => {
  const obj = {}
  if (["filters", "visibleFilters", "visibleFiltersAdmin"].includes(key)) {
    if (key === "filters") obj.filtersLastUpd = new Date().getTime()
    obj[key] = { ...state?.[key], ...value }
  } else {
    obj[key] = value
  }
  return state.merge(obj)
}

export const clearRequestsDigitalReceiptFilters = (state, { payload: data }) =>
  state.merge({ filters: { ...INITIAL_STATE.filters, ...data } })

const logout = (state) =>
  state.merge({
    ...INITIAL_STATE,
    filters: state.filters,
    columnsAdmin: state.columnsAdmin,
    visibleFiltersAdmin: state.visibleFiltersAdmin
  })

/* ------------- Hookup Reducers To Types ------------- */
export const reducer = createReducer(INITIAL_STATE, {
  [Types.GET_REQUESTS_DIGITAL_RECEIPT_REQUEST]: getRequestsDigitalReceiptRequest,
  [Types.GET_REQUESTS_DIGITAL_RECEIPT_SUCCESS]: getRequestsDigitalReceiptSuccess,

  [Types.UPDATE_REQUESTS_DIGITAL_RECEIPT_STATUS_REQUEST]: updateRequestsDigitalReceiptStatusRequest,
  [Types.UPDATE_REQUESTS_DIGITAL_RECEIPT_STATUS_SUCCESS]: updateRequestsDigitalReceiptStatusSuccess,

  [Types.REVIEW_DIGITAL_RECEIPT_UPDATE_REQUEST]: reviewDigitalReceiptUpdateRequest,
  [Types.REVIEW_DIGITAL_RECEIPT_UPDATE_SUCCESS]: reviewDigitalReceiptUpdateSuccess,

  [Types.REQUESTS_DIGITAL_RECEIPT_FAILURE]: requestsDigitalReceiptFailure,

  [Types.CLEAR_REQUESTS_DIGITAL_RECEIPT_FILTERS]: clearRequestsDigitalReceiptFilters,
  [Types.REQUESTS_DIGITAL_RECEIPT_SET_PROP]: requestsDigitalReceiptSetProp,

  [Types.LOGOUT]: logout
})
